// @ts-nocheck
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useDashboardAnalytics } from "@/hooks/api-hooks";
import { useAuth } from "@/contexts/auth-context";
import { UsageDashboardWidget } from "@/components/dashboard/UsageDashboardWidget";
import {
  Users,
  MessageSquare,
  Calendar,
  TrendingUp,
  Award,
  Clock,
  CheckCircle2,
  Target,
  Activity,
  Star,
  Heart,
  AlertTriangle
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

export function DashboardPage() {

  // Use API hook for dashboard data and auth
  const { user } = useAuth();

  const normalizedRole = (user?.role ?? user?.organizationRole ?? '').toLowerCase();
  const isStaff = normalizedRole === 'staff';

  const { data: dashboardData, isLoading, error } = useDashboardAnalytics({
    role: normalizedRole || undefined // Pass user role to get role-specific data
  });

  // Extract metrics from API data
  const metrics = dashboardData?.data || {};
  const realTime = metrics.realTime || {};
  const today = metrics.today || {};
  const thisWeek = metrics.thisWeek || {};
  const monthlyComparison = metrics.monthlyComparison || {};
  const systemHealth = metrics.systemHealth || {};
  const recentActivity = metrics.recentActivity || [];
  const upcomingSessionsDetails = metrics.upcomingSessionsDetails || [];
  const personalPerformance = metrics.personalPerformance || {};
  const organizationMetrics = metrics.organizationMetrics || {};
  const topPerformers = metrics.topPerformers || {};

  // Debug logging for client side
  console.log('=== CLIENT SIDE DASHBOARD DEBUG ===');
  console.log('User Role:', normalizedRole);
  console.log('User ID:', user?.id);
  console.log('Full Dashboard Data:', JSON.stringify(dashboardData, null, 2));
  console.log('Organization Metrics:', JSON.stringify(organizationMetrics, null, 2));
  console.log('Real Time Metrics:', JSON.stringify(realTime, null, 2));
  console.log('System Health:', JSON.stringify(systemHealth, null, 2));
  console.log('Monthly Comparison:', JSON.stringify(monthlyComparison, null, 2));
  console.log('Recent Activity Count:', recentActivity.length);
  console.log('Upcoming Sessions Count:', upcomingSessionsDetails.length);
  console.log('Dashboard error:', error);

  // Format date helper
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);
  };

  // Format time helper
  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "numeric",
    }).format(date);
  };

  // Calculate completion percentage
  const getCompletionPercentage = (completed: number, total: number) => {
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  // Get health color based on score - theme aware
  const getHealthColor = (score: number) => {
    if (score >= 85) return "text-success";
    if (score >= 70) return "text-warning";
    return "text-destructive";
  };

  // Get rating color based on score (5-point scale) - theme aware
  const getRatingColor = (rating: number) => {
    if (rating >= 4.0) return "text-success";
    if (rating >= 3.0) return "text-primary";
    if (rating >= 2.0) return "text-warning";
    return "text-destructive";
  };

  // Theme-Aware Circular Progress Component
  const CircularProgress = ({
    value,
    max = 100,
    size = 120,
    strokeWidth = 8,
    variant = "primary" // primary, success, warning, danger
  }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const progress = ((value || 0) / max) * circumference;

    // Theme-aware color classes using our palette
    const getProgressColor = () => {
      switch (variant) {
        case "success":
          return "stroke-success";
        case "warning":
          return "stroke-warning";
        case "danger":
          return "stroke-destructive";
        case "secondary":
          return "stroke-muted-foreground";
        default:
          return "stroke-primary"; // Use primary (soft blue) from our palette
      }
    };

    return (
      <div className="relative inline-flex items-center justify-center">
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            className="stroke-border"
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            className={`${getProgressColor()} transition-all duration-1000 ease-out`}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset={circumference - progress}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-bold text-foreground">
            {max === 5 ? `${(value || 0).toFixed(1)}` : max === 10 ? `${(value || 0).toFixed(1)}` : `${Math.round(value || 0)}%`}
          </span>
        </div>
      </div>
    );
  };

  // Show loading state while user is not authenticated or data is loading
  if (isLoading || !user) {
    return (
      <div className="min-h-screen bg-background">
        <div className="space-y-8 p-6 max-w-7xl mx-auto">
          <div className="bg-surface rounded-xl p-8 border border-border animate-pulse shadow-premium">
            <div className="flex items-start justify-between">
              <div className="space-y-3">
                <div className="h-8 bg-muted/30 rounded-lg w-80"></div>
                <div className="h-5 bg-muted/20 rounded-lg w-96"></div>
              </div>
              <div className="bg-muted/10 rounded-lg p-4 border border-border">
                <div className="w-24 h-12 bg-muted/20 rounded"></div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-card border border-border p-6 rounded-xl animate-pulse shadow-premium hover:shadow-premium-lg transition-all duration-300">
                <div className="flex flex-col items-center text-center space-y-6">
                  <div className="flex items-center gap-3 w-full">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg"></div>
                    <div className="space-y-2 flex-1">
                      <div className="w-24 h-4 bg-muted/40 rounded"></div>
                      <div className="w-32 h-3 bg-muted/30 rounded"></div>
                    </div>
                  </div>
                  <div className="w-28 h-28 bg-primary/5 rounded-full border-2 border-primary/10"></div>
                  <div className="w-20 h-3 bg-muted/30 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <div className="space-y-8 p-6 max-w-6xl mx-auto">
          <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-12 text-center shadow-premium">
            <div className="bg-destructive/10 rounded-full p-4 w-20 h-20 mx-auto mb-6">
              <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
            </div>
            <h2 className="text-headline text-foreground mb-3">Failed to load dashboard</h2>
            <p className="text-subtitle text-foreground-secondary mb-6">{error.message}</p>
            <p className="text-body text-foreground-muted">Please refresh the page to try again</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="space-y-8 p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-surface rounded-xl border border-border shadow-premium transition-all duration-300 hover:shadow-premium-lg">
          <div className="p-8">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <h1 className="text-headline text-foreground animate-in fade-in">
                  Welcome back, {user?.firstName || 'User'}
                </h1>
                <p className="text-subtitle text-foreground-secondary">
                  {isStaff ?
                    "Here's your personal development overview" :
                    "Here's your organization overview and team insights"
                  }
                </p>
              </div>
              <div className="text-right bg-muted/30 rounded-lg p-4 border border-border">
                <div className="text-caption text-foreground-muted font-medium uppercase tracking-wide">{formatDate(new Date())}</div>
                <div className="text-subtitle font-semibold text-foreground">{formatTime(new Date())}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Plan Usage Dashboard - Available to all users */}
        <div className="mt-8">
          <UsageDashboardWidget />
        </div>

        {/* Visual Charts Section - Role-specific */}
        {isStaff ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Overall Feedback Rating Chart */}
            <Card className="bg-card border border-border shadow-premium hover:shadow-premium-lg transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-primary/10 rounded-lg p-2.5 w-10 h-10 flex items-center justify-center">
                    <Star className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-subtitle font-semibold text-foreground">Overall Rating</h3>
                    <p className="text-caption text-foreground-muted">From feedback received</p>
                  </div>
                </div>
                <div className="flex items-center justify-center mb-6">
                  <CircularProgress
                    value={personalPerformance?.overallFeedbackRating || 0}
                    max={5}
                    size={110}
                    variant="primary"
                  />
                </div>
                <div className="text-center">
                  <div className="text-caption text-foreground-muted font-medium">
                    Based on {personalPerformance?.totalFeedbackRatings || 0} responses
                  </div>
                </div>
              </div>
            </Card>

            {/* Happiness Chart */}
            <Card className="bg-card border border-border shadow-premium hover:shadow-premium-lg transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-success/10 rounded-lg p-2.5 w-10 h-10 flex items-center justify-center">
                    <Heart className="h-5 w-5 text-success" />
                  </div>
                  <div>
                    <h3 className="text-subtitle font-semibold text-foreground">Happiness</h3>
                    <p className="text-caption text-foreground-muted">Your overall happiness</p>
                  </div>
                </div>
                <div className="flex items-center justify-center mb-6">
                  <CircularProgress
                    value={personalPerformance?.overallHappiness ? personalPerformance.overallHappiness / 20 : 0}
                    max={5}
                    size={110}
                    variant="success"
                  />
                </div>
                <div className="text-center">
                  <div className="text-caption text-foreground-muted font-medium">
                    Combined feedback & mood
                  </div>
                </div>
              </div>
            </Card>

            {/* Personal Action Items Chart */}
            <Card className="bg-card border border-border shadow-premium hover:shadow-premium-lg transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-warning/10 rounded-lg p-2.5 w-10 h-10 flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-warning" />
                  </div>
                  <div>
                    <h3 className="text-subtitle font-semibold text-foreground">Action Items</h3>
                    <p className="text-caption text-foreground-muted">Your overdue items</p>
                  </div>
                </div>
                <div className="flex items-center justify-center mb-6">
                  <CircularProgress
                    value={100 - Math.min((systemHealth.overdueActionItems || 0) * 10, 100)}
                    max={100}
                    size={110}
                    variant={systemHealth.overdueActionItems > 5 ? "danger" : systemHealth.overdueActionItems > 2 ? "warning" : "success"}
                  />
                </div>
                <div className="text-center">
                  <div className="text-caption text-foreground-muted font-medium">
                    {systemHealth.overdueActionItems || 0} overdue items
                  </div>
                </div>
              </div>
            </Card>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Team Happiness */}
            <Card className="bg-card border border-border shadow-premium hover:shadow-premium-lg transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-success/10 rounded-lg p-2.5 w-10 h-10 flex items-center justify-center">
                    <Heart className="h-5 w-5 text-success" />
                  </div>
                  <div>
                    <h3 className="text-subtitle font-semibold text-foreground">Team Happiness</h3>
                    <p className="text-caption text-foreground-muted">Average team mood</p>
                  </div>
                </div>
                <div className="flex items-center justify-center mb-6">
                  <CircularProgress
                    value={organizationMetrics?.teamHappiness || 0}
                    max={5}
                    size={110}
                    variant="success"
                  />
                </div>
                <div className="text-center">
                  <div className="text-caption text-foreground-muted font-medium">
                    From 1-on-1 sessions
                  </div>
                </div>
              </div>
            </Card>

            {/* Team Feedback Rating */}
            <Card className="bg-card border border-border shadow-premium hover:shadow-premium-lg transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-primary/10 rounded-lg p-2.5 w-10 h-10 flex items-center justify-center">
                    <Star className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-subtitle font-semibold text-foreground">Team Rating</h3>
                    <p className="text-caption text-foreground-muted">Average feedback score</p>
                  </div>
                </div>
                <div className="flex items-center justify-center mb-6">
                  <CircularProgress
                    value={organizationMetrics?.teamFeedbackRating || 0}
                    max={10}
                    size={110}
                    variant="primary"
                  />
                </div>
                <div className="text-center">
                  <div className="text-caption text-foreground-muted font-medium">
                    From {organizationMetrics?.totalRatings || 0} responses
                  </div>
                </div>
              </div>
            </Card>

            {/* 1-on-1 Frequency */}
            <Card className="bg-card border border-border shadow-premium hover:shadow-premium-lg transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-success/10 rounded-lg p-2.5 w-10 h-10 flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-success" />
                  </div>
                  <div>
                    <h3 className="text-subtitle font-semibold text-foreground">1-on-1 Rate</h3>
                    <p className="text-caption text-foreground-muted">This week completion</p>
                  </div>
                </div>
                <div className="flex items-center justify-center mb-6">
                  <CircularProgress
                    value={thisWeek?.completionRate || 0}
                    max={100}
                    size={110}
                    variant="success"
                  />
                </div>
                <div className="text-center">
                  <div className="text-caption text-foreground-muted font-medium">
                    {thisWeek?.completedSessions || 0} of {thisWeek?.sessions || 0} completed
                  </div>
                </div>
              </div>
            </Card>

            {/* Team Issues */}
            <Card className="bg-card border border-border shadow-premium hover:shadow-premium-lg transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-warning/10 rounded-lg p-2.5 w-10 h-10 flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-warning" />
                  </div>
                  <div>
                    <h3 className="text-subtitle font-semibold text-foreground">Team Issues</h3>
                    <p className="text-caption text-foreground-muted">Overdue action items</p>
                  </div>
                </div>
                <div className="flex items-center justify-center mb-6">
                  <CircularProgress
                    value={100 - Math.min((systemHealth.overdueActionItems || 0) * 2, 100)}
                    max={100}
                    size={110}
                    variant={systemHealth.overdueActionItems > 10 ? "danger" : systemHealth.overdueActionItems > 5 ? "warning" : "success"}
                  />
                </div>
                <div className="text-center">
                  <div className="text-caption text-foreground-muted font-medium">
                    {systemHealth.overdueActionItems || 0} items need attention
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Quick Overview Cards - Role-specific */}
        {isStaff ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Activity */}
            <Card className="bg-card border border-border shadow-premium hover:shadow-premium-lg transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-primary/10 rounded-lg p-2.5 w-10 h-10 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-subtitle font-semibold text-foreground">Recent Activity</h3>
                    <p className="text-caption text-foreground-muted">Your latest interactions</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {recentActivity.slice(0, 4).map((activity: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border/50 hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                        <div>
                          <div className="text-body font-medium text-foreground">
                            {activity.type === 'session' ? 'Session' : 'Feedback'} {activity.action}
                          </div>
                          <div className="text-caption text-foreground-muted">
                            {activity.title}
                          </div>
                        </div>
                      </div>
                      <div className="text-caption text-foreground-muted font-medium">
                        {formatRelativeTime(new Date(activity.timestamp))}
                      </div>
                    </div>
                  ))}
                  {recentActivity.length === 0 && (
                    <div className="text-center py-8 text-foreground-muted">
                      <div className="text-body">No recent activity</div>
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* Upcoming Sessions */}
            <Card className="bg-card border border-border shadow-premium hover:shadow-premium-lg transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-primary/10 rounded-lg p-2.5 w-10 h-10 flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-subtitle font-semibold text-foreground">Your Upcoming 1-on-1s</h3>
                    <p className="text-caption text-foreground-muted">Next scheduled sessions</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {upcomingSessionsDetails && upcomingSessionsDetails.length > 0 ? (
                    upcomingSessionsDetails.slice(0, 3).map((session: any) => {
                      const sessionDate = new Date(session.scheduledDate);
                      return (
                        <div key={session.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border/50 hover:bg-muted/50 transition-colors">
                          <div className="flex items-center gap-4">
                            <div className="text-center min-w-[48px] bg-primary/5 rounded-lg p-2">
                              <div className="text-title font-bold text-foreground">{sessionDate.getDate()}</div>
                              <div className="text-caption text-foreground-muted font-medium uppercase tracking-wide">
                                {sessionDate.toLocaleString('default', { month: 'short' })}
                              </div>
                            </div>
                            <div>
                              <div className="text-body font-medium text-foreground">
                                1-on-1 with {session.manager.firstName} {session.manager.lastName}
                              </div>
                              <div className="text-caption text-foreground-muted">
                                {formatTime(sessionDate)} • {session.duration}min
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-foreground-muted">
                      <Calendar className="mx-auto h-12 w-12 text-foreground-muted opacity-20 mb-3" />
                      <p className="text-body">
                        No upcoming sessions scheduled
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Team Performance Summary */}
            <Card className="bg-card border border-border shadow-premium hover:shadow-premium-lg transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-primary/10 rounded-lg p-2.5 w-10 h-10 flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-subtitle font-semibold text-foreground">Team Performance</h3>
                    <p className="text-caption text-foreground-muted">This month comparison</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-muted/20 rounded-xl border border-border transition-all duration-200 hover:bg-muted/30">
                    <div>
                      <div className="text-body font-semibold text-foreground">1-on-1 Sessions</div>
                      <div className="text-caption text-foreground-muted font-medium">vs. last month</div>
                    </div>
                    <div className="text-right">
                      <div className="text-title font-bold text-primary">
                        {monthlyComparison?.sessions?.current || 0}
                      </div>
                      <div className={`text-caption font-bold ${
                        (monthlyComparison?.sessions?.growth || 0) >= 0 ? 'text-success' : 'text-destructive'
                      }`}>
                        {(monthlyComparison?.sessions?.growth || 0) >= 0 ? '+' : ''}
                        {monthlyComparison?.sessions?.growth || 0}%
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-success/10 rounded-xl border border-success/30 transition-smooth hover:bg-success/15">
                    <div>
                      <div className="text-sm font-bold text-success">Feedback Cycles</div>
                      <div className="text-xs text-success/80 font-medium">vs. last month</div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-success">
                        {monthlyComparison?.feedback?.current || 0}
                      </div>
                      <div className={`text-xs font-bold ${
                        (monthlyComparison?.feedback?.growth || 0) >= 0 ? 'text-success' : 'text-destructive'
                      }`}>
                        {(monthlyComparison?.feedback?.growth || 0) >= 0 ? '+' : ''}
                        {monthlyComparison?.feedback?.growth || 0}%
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-primary/10 rounded-xl border border-primary/30 transition-smooth hover:bg-primary/15">
                    <div>
                      <div className="text-sm font-bold text-primary">Active Members</div>
                      <div className="text-xs text-primary/80 font-medium">vs. last month</div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-primary">
                        {monthlyComparison?.users?.current || 0}
                      </div>
                      <div className={`text-xs font-bold ${
                        (monthlyComparison?.users?.growth || 0) >= 0 ? 'text-success' : 'text-destructive'
                      }`}>
                        {(monthlyComparison?.users?.growth || 0) >= 0 ? '+' : ''}
                        {monthlyComparison?.users?.growth || 0}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Upcoming Team Sessions */}
            <Card className="bg-card border border-border shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-primary/10 rounded-lg p-2">
                    <Calendar className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Upcoming Team 1-on-1s</h3>
                    <p className="text-xs text-foreground-muted">Next scheduled sessions</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {upcomingSessionsDetails && upcomingSessionsDetails.length > 0 ? (
                    upcomingSessionsDetails.slice(0, 4).map((session: any) => {
                      const sessionDate = new Date(session.scheduledDate);
                      return (
                        <div key={session.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="text-center min-w-[40px]">
                              <div className="text-lg font-bold">{sessionDate.getDate()}</div>
                              <div className="text-xs text-muted-foreground">
                                {sessionDate.toLocaleString('default', { month: 'short' })}
                              </div>
                            </div>
                            <div>
                              <div className="font-medium text-sm">
                                {session.manager.firstName} {session.manager.lastName}
                                <span className="text-muted-foreground"> → </span>
                                {session.employee.firstName} {session.employee.lastName}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {formatTime(sessionDate)} • {session.duration}min
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      <Calendar className="mx-auto h-12 w-12 text-muted-foreground opacity-20 mb-2" />
                      <p className="text-sm">
                        No upcoming team sessions scheduled
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Performance/Health Section - Role-specific */}
        {isStaff ? (
          <Card className="bg-card border border-border shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-primary/10 rounded-lg p-2">
                  <Star className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Personal Performance</h3>
                  <p className="text-xs text-foreground-muted">Your development metrics</p>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                {/* Pending Feedback */}
                <div className="text-center p-4 bg-warning/10 rounded-lg border border-warning/20">
                  <div className="text-2xl font-bold text-amber-600 mb-1">
                    {personalPerformance?.pendingFeedback || 0}
                  </div>
                  <div className="text-sm text-amber-600 font-medium">Pending Feedback</div>
                  <div className="text-xs text-muted-foreground mt-1">To complete</div>
                </div>

                {/* Given Feedback */}
                <div className="text-center p-4 bg-success/10 rounded-lg border border-success/20">
                  <div className="text-2xl font-bold text-success mb-1">
                    {personalPerformance?.givenFeedback || 0}
                  </div>
                  <div className="text-sm text-success font-medium">Given Feedback</div>
                  <div className="text-xs text-muted-foreground mt-1">Total provided</div>
                </div>

                {/* Productivity Rating */}
                <div className="text-center p-4 bg-primary/10 rounded-lg border border-primary/20">
                  <div className="text-2xl font-bold text-primary mb-1">
                    {personalPerformance?.productivity ? (personalPerformance.productivity / 20).toFixed(1) : '0.0'}/5.0
                  </div>
                  <div className="text-sm text-primary font-medium">Productivity Rating</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {personalPerformance?.completedActionItems || 0}/{personalPerformance?.totalActionItems || 0} tasks completed
                  </div>
                </div>

                {/* Mood Rating */}
                <div className="text-center p-4 bg-muted rounded-lg border border-border">
                  <div className="text-2xl font-bold text-primary mb-1">
                    {personalPerformance?.moodRating ? (personalPerformance.moodRating / 2).toFixed(1) : '0.0'}/5.0
                  </div>
                  <div className="text-sm text-primary font-medium">Mood Rating</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    From {personalPerformance?.totalMoodRatings || 0} sessions
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Team Health & Key Metrics */}
            <Card className="bg-card border border-border shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-primary/10 rounded-lg p-2">
                    <Activity className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Team Health & Insights</h3>
                    <p className="text-xs text-foreground-muted">Key performance indicators</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* System Health Score */}
                  <div className="text-center p-4 bg-success/10 rounded-lg border border-success/20">
                    <div className={`text-3xl font-bold mb-1 ${
                      systemHealth.healthScore >= 85 ? 'text-success' :
                      systemHealth.healthScore >= 70 ? 'text-warning' : 'text-destructive'
                    }`}>
                      {systemHealth.healthScore || 0}%
                    </div>
                    <div className="text-sm font-medium text-foreground">System Health</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Overall system performance
                    </div>
                  </div>

                  {/* Active Team Members */}
                  <div className="text-center p-4 bg-muted rounded-lg border border-border">
                    <div className="text-3xl font-bold text-primary mb-1">
                      {realTime?.activeUsers || 0}
                    </div>
                    <div className="text-sm font-medium text-primary">Active Today</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Members active last 30min
                    </div>
                  </div>

                  {/* 1-on-1 Frequency Insight */}
                  <div className="text-center p-4 bg-primary/10 rounded-lg border border-primary/20">
                    <div className="text-3xl font-bold text-primary mb-1">
                      {Math.round((thisWeek?.sessions || 0) / 7 * 30) || 0}
                    </div>
                    <div className="text-sm font-medium text-primary">Monthly Rate</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Projected 1-on-1s per month
                    </div>
                  </div>

                  {/* Team Issues */}
                  <div className="text-center p-4 bg-warning/10 rounded-lg border border-warning/20">
                    <div className={`text-3xl font-bold mb-1 ${
                      systemHealth.overdueActionItems === 0 ? 'text-success' :
                      systemHealth.overdueActionItems <= 5 ? 'text-warning' : 'text-destructive'
                    }`}>
                      {systemHealth.overdueActionItems || 0}
                    </div>
                    <div className="text-sm font-medium text-warning">Overdue Items</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Items needing attention
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Top Performers */}
            {topPerformers && (topPerformers.managers?.length > 0 || topPerformers.teams?.length > 0) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Managers */}
                {topPerformers.managers?.length > 0 && (
                  <Card className="bg-card border border-border shadow-sm hover:shadow-md transition-shadow duration-200">
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="bg-warning/10 rounded-lg p-2">
                          <Award className="h-5 w-5 text-warning" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-foreground">Top Performing Managers</h3>
                          <p className="text-xs text-foreground-muted">Best completion rates</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        {topPerformers.managers.slice(0, 3).map((manager: any, index: number) => (
                          <div key={manager.user.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                index === 0 ? 'bg-warning/10 text-warning' :
                                index === 1 ? 'bg-muted text-muted-foreground' : 'bg-destructive/10 text-destructive'
                              }`}>
                                {index + 1}
                              </div>
                              <div>
                                <div className="font-medium text-sm">
                                  {manager.user.firstName} {manager.user.lastName}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {manager.completedSessions} of {manager.totalSessions} sessions
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-success">
                                {manager.completionRate}%
                              </div>
                              <div className="text-xs text-muted-foreground">completion</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Card>
                )}

                {/* Top Teams */}
                {topPerformers.teams?.length > 0 && (
                  <Card className="bg-card border border-border shadow-sm hover:shadow-md transition-shadow duration-200">
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="bg-primary/10 rounded-lg p-2">
                          <Users className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-foreground">Most Active Teams</h3>
                          <p className="text-xs text-foreground-muted">Highest engagement levels</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        {topPerformers.teams.slice(0, 3).map((team: any, index: number) => (
                          <div key={team.team.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                index === 0 ? 'bg-warning/10 text-warning' :
                                index === 1 ? 'bg-muted text-muted-foreground' : 'bg-destructive/10 text-destructive'
                              }`}>
                                {index + 1}
                              </div>
                              <div>
                                <div className="font-medium text-sm">
                                  {team.team.name}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {team.memberCount} members
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-primary">
                                {team.activityScore.toFixed(1)}
                              </div>
                              <div className="text-xs text-muted-foreground">activity score</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}

      </div>

    </div>
  );

  function formatRelativeTime(date: Date) {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return "just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  }
}
